import _ from 'lodash';
import { irrigationEndTimeForWire } from './irrigation_utils';
import { getControllerApiVersion, IsExcludedBasedOnTypeOrProject, makeScheduleTankFilling } from './utils';
import { SCHEDULE_PROTOCOL_VERSION, SCHEDULING_UI_ENABLED } from '../Constants/constants';

import { restoreSettingsDraft } from './settings_draft';

// ScheduledLightChannels stores a complete lighting block per edited phase.
// Empty channel/point arrays are intentional replacements, not partial edits.
export function restoreScheduleDraft(baseline, draft = {}) {
  return restoreSettingsDraft(baseline, draft, (current, retained, key) =>
    key === 'lighting' ? _.cloneDeep(retained) : undefined
  );
}

export function supportsScheduling(controller) {
  return getControllerApiVersion(controller).localeCompare('BAID') >= 0 &&
    !IsExcludedBasedOnTypeOrProject(controller, 'scheduling', 'tabs');
}

// Preview access lifts the GUI release hold without changing controller support.
export function isSchedulingUiEnabled(previewFeaturesEnabled) {
  return SCHEDULING_UI_ENABLED || Boolean(previewFeaturesEnabled);
}

export function supportsScheduleProtocol(controller, values) {
  return supportsScheduling(controller) &&
    values?.schedule_meta?.protocol_version === SCHEDULE_PROTOCOL_VERSION;
}

export function scheduleShortNameLimit() {
  return 19;
}

export function validScheduleName(value, limit) {
  if (typeof value !== 'string' || value.includes('\0')) return false;
  // Blob counts encoded bytes; reject lone surrogates instead of silently
  // replacing them during UTF-8 encoding.
  return (
    new Blob([value]).size <= limit &&
    !Array.from(value).some((character) => {
      const code = character.codePointAt(0);
      return code >= 0xd800 && code <= 0xdfff;
    })
  );
}

export function validScheduleNames(values, shortLimit) {
  return (
    validScheduleName(values?.schedule_meta?.name_short ?? '', shortLimit) &&
    (values?.schedule_values?.scheduling_values ?? [])
      .filter((phase) => !phase._deleted)
      .every(
        (phase) =>
          validScheduleName(phase.name_phase_short ?? '', shortLimit) &&
          validScheduleName(phase.name_phase_full ?? '', 31)
      )
  );
}

export function normalizeScheduleMetadata(metadata) {
  if (!metadata || metadata.protocol_version !== SCHEDULE_PROTOCOL_VERSION) return metadata;
  const result = { ...metadata };
  Object.keys(result)
    .filter((key) => key.startsWith('schedule_') && key.endsWith('_enabled'))
    .forEach((key) => {
      if (typeof result[key] === 'boolean') result[key] = Number(result[key]);
    });
  return result;
}

export function normalizeScheduleSettings(settings) {
  if (!settings) return settings;
  const metadata = normalizeScheduleMetadata(settings.schedule_meta);
  return _.isEqual(metadata, settings.schedule_meta)
    ? settings
    : { ...settings, schedule_meta: metadata };
}

export function scheduledDayRange(timing, metadata, phase) {
  const minutes = (value, fallback) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const [hours, minutes] = value.split(':').map(Number);
      return hours * 60 + minutes;
    }
    return fallback;
  };
  const ordinaryStart =
    Number(timing?.start_day_hour ?? 0) * 60 + Number(timing?.start_day_min ?? 0);
  const ordinaryEnd = Number(timing?.end_day_hour ?? 0) * 60 + Number(timing?.end_day_min ?? 0);
  const start = metadata.schedule_day_night_enabled
    ? minutes(phase.time_day_start, ordinaryStart)
    : ordinaryStart;
  let end = metadata.schedule_day_night_enabled
    ? minutes(phase.time_day_end, ordinaryEnd)
    : ordinaryEnd;
  if (end <= start) end += 1440;
  return { start, end };
}

// IDs label phases; recipe order and duration slots remain array positions.
export function nextSchedulePhaseId(phases) {
  const ids = new Set(phases.map((phase) => phase.phase_id));
  let id = 1;
  while (ids.has(id)) id += 1;
  return id;
}

export function schedulingSettingsReady(values) {
  const metadata = values?.schedule_meta;
  const recipe = values?.schedule_values;
  if (!metadata || !Array.isArray(recipe?.scheduling_values)) return false;
  // Versioned readback can arrive in separate updates. Do not combine metadata
  // for one revision with phases from another or open a partially received recipe.
  return (
    metadata.protocol_version === SCHEDULE_PROTOCOL_VERSION &&
    (metadata.recipe_revision === recipe.recipe_revision &&
      Number(metadata.active_phase_cnt) === Number(recipe.schedule_values_count) &&
      recipe.scheduling_values.length === Number(recipe.schedule_values_count))
  );
}


function scheduleBoolean(value) {
  return value === true || value === 1;
}

export function transformScheduleValuesForSend(settings) {
  const updatedSettings = _.cloneDeep(settings);

  updatedSettings.scheduling_values.forEach((setting) => {
    if (setting.lighting?.schema_version === 1)
      setting.lighting.channels = setting.lighting.channels.map((c) => ({
        subzone: c.subzone,
        channel: c.channel,
        kind: c.kind,
        maximum_override: c.maximum_override,
        maximum: c.maximum,
        enable_override: c.enable_override,
        enabled: c.enabled,
        points: c.points.map(({ offset_minutes, level, anchor, interpolation }) => ({
          offset_minutes,
          level,
          anchor,
          interpolation
        }))
      }));
    setting.irrigation_settings = setting.irrigation_settings.map((irrigation) => {
      const scheduledIrrigation = {
        enable_irrigation: Number(scheduleBoolean(irrigation.enable_irrigation)),
        selected_tank_id: irrigation.selected_tank_id,
        irrigation_time_exec_gap_days: irrigation.irrigation_time_exec_gap_days,
        irrigation_p0_enable: scheduleBoolean(irrigation.irrigation_p0_enable),
        irrigation_p0_enable_timeout: scheduleBoolean(irrigation.irrigation_p0_enable_timeout),
        irrigation_p0_dry_back_abs: irrigation.irrigation_p0_dry_back_abs,
        irrigation_p0_phase_timeout_minutes: irrigation.irrigation_p0_phase_timeout_minutes,
        irrigation_p1_shot_size_vwc_rel: irrigation.irrigation_p1_shot_size_vwc_rel,
        irrigation_p1_target_vwc_abs: irrigation.irrigation_p1_target_vwc_abs,
        irrigation_p1_auto_vwc_exec_gap_days: irrigation.irrigation_p1_auto_vwc_exec_gap_days ?? 0,
        irrigation_p1_wait_after_shot_minutes: irrigation.irrigation_p1_wait_after_shot_minutes,
        irrigation_p2_offset_target_vwc: irrigation.irrigation_p2_offset_target_vwc,
        irrigation_p2_target_vwc_abs: irrigation.irrigation_p2_target_vwc_abs,
        irrigation_p2_dry_back_abs: irrigation.irrigation_p2_dry_back_abs,
        irrigation_p2_wait_after_shot_s: irrigation.irrigation_p2_wait_after_shot_s,
        irrigation_p2_interval_on_duration_seconds:
          irrigation.irrigation_p2_interval_on_duration_seconds,
        irrigation_p2_interval_off_duration_minutes:
          irrigation.irrigation_p2_interval_off_duration_minutes,
        irrigation_p2_end_before_sunset: irrigation.irrigation_p2_end_before_sunset,
        irrigation_p3_min_vwc_abs: irrigation.irrigation_p3_min_vwc_abs
      };

      scheduledIrrigation.irrigation_p2_end_before_sunset = irrigationEndTimeForWire(
        scheduledIrrigation.irrigation_p2_end_before_sunset
      );
      return scheduledIrrigation;
    });

    setting.irrigation_events?.forEach((event) => {
      event.enabled = scheduleBoolean(event.enabled);
    });
    // JSON represents event count by the array itself. The controller DTO
    // readback does not echo the internal count; do not compare a GUI-only field.
    delete setting.irrigation_event_count;
    // Schema zero inherits ordinary lighting and is omitted by the DTO.
    if (!setting.lighting?.schema_version) delete setting.lighting;
    delete setting._lighting_draft;
  });

  return {
    ...updatedSettings,
    protocol_version: settings.protocol_version ?? SCHEDULE_PROTOCOL_VERSION,
    schedule_values_count: updatedSettings.scheduling_values.length,
    scheduling_values: updatedSettings.scheduling_values
  };
}

export function scheduleRuntimeInfo(values) {
  const recipe = values?.schedule_values;
  if (values?.schedule_meta?.protocol_version !== SCHEDULE_PROTOCOL_VERSION)
    return null;
  if (!schedulingSettingsReady(values)) return { status: 'schedule_loading' };
  const status =
    {
      0: 'schedule_runtime_disabled',
      1: 'schedule_runtime_active',
      2: 'schedule_runtime_completed',
      3: 'schedule_runtime_invalid'
    }[recipe.runtime_status] ?? 'schedule_runtime_unknown';
  const phase =
    [1, 2].includes(recipe.runtime_status) &&
    recipe.active_recipe_revision === recipe.recipe_revision
      ? recipe.scheduling_values[recipe.active_phase_index]
      : undefined;
  return {
    status,
    phaseNumber: phase ? recipe.active_phase_index + 1 : undefined,
    phaseName: phase?.name_phase_full || phase?.name_phase_short || '',
    phaseId: phase?.phase_id,
    recipeRevision: recipe.recipe_revision
  };
}

export function schedulePath(controller, parent, phase) {
  const zone = parent === controller ? '' : `/zones/zone_${encodeURIComponent(parent.zone_id)}`;
  const base = `/controller/${encodeURIComponent(controller.id)}${zone}/scheduling`;
  return phase?.phaseId === undefined
    ? base
    : `${base}?phase=${phase.phaseId}&recipe=${phase.recipeRevision}`;
}

export function activeSchedule(controller, parent, values = parent.settings?.scheduling) {
  if (!controller.access_rights?.scheduling?.read || !supportsScheduleProtocol(controller, values))
    return null;
  const runtime = scheduleRuntimeInfo(values);
  if (
    !runtime?.phaseNumber ||
    !values.schedule_meta.schedule_enabled ||
    !values.schedule_values.recipe_valid
  )
    return null;
  return {
    ...runtime,
    metadata: values.schedule_meta,
    phase: values.schedule_values.scheduling_values[values.schedule_values.active_phase_index],
    href: schedulePath(controller, parent, runtime),
    scheduleHref: schedulePath(controller, parent)
  };
}

const scheduledClimateFields = {
  humidity: ['humidity_sp_day', 'humidity_sp_night'],
  main_temperature: ['temp_sp_day', 'temp_sp_night'],
  vpd: ['vpd_sp', 'vpd_sp_night', 'vpd_calc_enable'],
  heating_mat: [
    'temp_sp_heating_mat_day_abs',
    'temp_sp_heating_mat_night_abs',
    'temp_sp_heating_mat_day_rel',
    'temp_sp_heating_mat_night_rel',
    'toggle_temp_heating_mat_day',
    'toggle_temp_heating_mat_night'
  ],
  co2_temperature: ['temp_sp_co2']
};
const scheduledIrrigationFields = [
  'irrigation_events',
  'enable_irrigation',
  'selected_tank_id',
  'irrigation_time_exec_gap_days',
  'irrigation_p0_enable',
  'irrigation_p0_enable_timeout',
  'irrigation_p0_dry_back_abs',
  'irrigation_p0_phase_timeout_minutes',
  'irrigation_p1_shot_size_vwc_rel',
  'irrigation_p1_target_vwc_abs',
  'irrigation_p1_auto_vwc_exec_gap_days',
  'irrigation_p1_wait_after_shot_minutes',
  'irrigation_p2_offset_target_vwc',
  'irrigation_p2_target_vwc_abs',
  'irrigation_p2_dry_back_abs',
  'irrigation_p2_wait_after_shot_s',
  'irrigation_p2_interval_on_duration_seconds',
  'irrigation_p2_interval_off_duration_minutes',
  'irrigation_p2_end_before_sunset',
  'irrigation_p3_min_vwc_abs'
];

// Field ownership is shared by the active-phase presentation and its editing adapter.
export function scheduledSetting(context, category, path, index) {
  if (!context) return null;
  const metadata = context.metadata;
  const selected = (name, bit) =>
    Boolean(metadata[`schedule_${name}_enabled`]) &&
    (bit === undefined || Boolean(metadata[`schedule_${name}_bitmap`] & (1 << bit)));
  const field = path?.split('.').pop();
  const subzone =
    index ?? Number(/^(?:irrigation|nutrient)_settings\[(\d+)\]/.exec(path)?.[1] ?? 0);
  let managed = false;
  if (category === 'climate')
    managed = Object.entries(scheduledClimateFields).some(
      ([name, fields]) => selected(name) && fields.includes(field)
    );
  if (category === 'co2')
    managed =
      (['co2_sp', 'co2_control'].includes(field) && selected('co2_concentration')) ||
      (field === 'temp_sp_co2' && selected('co2_temperature'));
  if (category === 'lights') {
    const match = /^lights_max(?:_([2-4]))?$/.exec(field);
    managed = Boolean(match && selected('lights', Number(match[1] ?? 1) - 1));
  }
  if (category === 'timing') {
    managed = /^(start|end)_day_(hour|min)$/.test(field) && selected('day_night');
    const interval = /^(?:time_)?interval(?:_([12]))?_/.exec(field);
    const event = /^(?:time_)?event([1-8])_/.exec(field);
    if (interval) managed = selected('intervals', Number(interval[1] ?? 1) - 1);
    if (event) managed = selected('events', Number(event[1]) - 1);
  }
  if (category === 'irrigation')
    managed =
      selected('irrigations', subzone) &&
      Boolean(context.phase.irrigation_settings?.[subzone]) &&
      scheduledIrrigationFields.includes(field);
  if (category === 'nutrition')
    managed =
      selected('nutrients', subzone) &&
      Boolean(context.phase.nutrient_settings?.[subzone]) &&
      (['ec_setpoint', 'ph_setpoint', 'temp_setpoint', 'pump_cnt_used', 'pumps'].includes(field) ||
        /(?:^|\.)pumps\[\d+\]\.(?:value|type|unit)$/.test(path));
  if (category === 'ventilation') {
    const match = /^circulation(?:\[(\d+)\]|\.(\d+))\.(day|night|co2)$/.exec(path);
    const row = match && Number(match[1] ?? match[2]);
    managed = Boolean(match && selected('circulation', row) && context.phase.circulation?.[row]);
  }
  if (category === 'nutrition') {
    const tank = /^tanks\.([0-7])\.(selected_route_id|refill_strategy|fill_timeout_seconds|planned_offset_minutes)$/.exec(path);
    if (tank) managed = selected('tank_filling', Number(tank[1])) &&
      Boolean(context.phase.tank_filling?.some((row) => row.tank_id === Number(tank[1])));
  }
  if (!managed) return null;
  // Fertigation latches settings for a running operation. Its applied phase is
  // not in telemetry, so do not identify it with the global current phase.
  return ['irrigation', 'nutrition'].includes(category)
    ? { ...context, managed: true, href: context.scheduleHref }
    : context;
}

// Ordinary forms and phase forms use the same scalar units. Their time fields
// have different shapes: ordinary pages edit clock parts, phases store clocks.
export function scheduledFieldBinding(context, category, path, index) {
  const target = scheduledSetting(context, category, path, index);
  if (!target) return null;
  let phasePath = path;
  let clockPart;
  let tankField;
  if (category === 'lights' && path === 'lights_max') phasePath = 'lights_max_1';
  if (category === 'nutrition') {
    const tank = /^tanks\.([0-7])\.(.+)$/.exec(path);
    tankField = tank;
    if (tank) phasePath = `tank_filling[${context.phase.tank_filling.findIndex((row) => row.tank_id === Number(tank[1]))}].${tank[2]}`;
    else if (!path.startsWith('nutrient_settings[')) phasePath = `nutrient_settings[${index ?? 0}].${path}`;
  }
  if (category === 'irrigation' && !path.startsWith('irrigation_settings[') && path !== 'irrigation_events')
    phasePath = `irrigation_settings[${index ?? 0}].${path}`;
  if (category === 'timing') {
    const day = /^(start|end)_day_(hour|min)$/.exec(path);
    const interval = /^(?:time_)?interval(?:_([12]))?_(on|off)_(day|night)_(hour|min|minute|sec)$/.exec(path);
    const event = /^(?:time_)?event([1-8])_(start|duration)_(hour|min|sec)$/.exec(path);
    if (day) { phasePath = `time_day_${day[1]}`; clockPart = day[2]; }
    if (interval) {
      phasePath = `time_interval_${interval[1] ?? 1}_${interval[2]}_${interval[3]}`;
      clockPart = interval[4];
    }
    if (event) { phasePath = `time_event${event[1]}_${event[2]}`; clockPart = event[3]; }
  }
  const partIndex = clockPart === 'hour' ? 0 : clockPart === 'sec' ? 2 : 1;
  return {
    ...target,
    path: phasePath,
    get(phase) {
      const value = tankField
        ? phase?.tank_filling?.find((row) => row.tank_id === Number(tankField[1]))?.[tankField[2]]
        : _.get(phase, phasePath);
      return clockPart ? Number(String(value ?? '00:00:00').split(':')[partIndex] ?? 0) : value;
    },
    set(phase, value) {
      if (!clockPart) return value;
      const parts = String(_.get(phase, phasePath) ?? '00:00:00').split(':');
      while (parts.length < 3) parts.push('00');
      parts[partIndex] = String(value).padStart(2, '0');
      return parts.join(':');
    }
  };
}

export function linkedSchedulePhase(values, search) {
  const query = new URLSearchParams(search);
  if (!query.has('phase')) return undefined;
  if (!schedulingSettingsReady(values)) return undefined;
  if (
    !query.has('recipe') ||
    Number(query.get('recipe')) !== values.schedule_values.recipe_revision
  )
    return -1;
  return values.schedule_values.scheduling_values.findIndex(
    (phase) => phase.phase_id === Number(query.get('phase'))
  );
}

// Events share one array, but scheduling selects individual irrigation rows.
// Keep every unselected row in its own draft when editing the combined view.
export function scheduledIrrigationEventRows(context) {
  return (context?.phase.irrigation_settings ?? []).flatMap((row, index) =>
    row && scheduledSetting(context, 'irrigation', 'irrigation_events', index) ? [index] : []
  );
}

export function projectScheduledIrrigationEvents(context, ordinaryEvents = []) {
  const selected = scheduledIrrigationEventRows(context);
  if (!selected.length) return _.cloneDeep(ordinaryEvents);
  return [
    ...ordinaryEvents.filter((event) => !selected.includes(Number(event.irrigation_index))),
    ...(context.phase.irrigation_events ?? []).filter((event) =>
      selected.includes(Number(event.irrigation_index)))
  ];
}

export function splitScheduledIrrigationEvents(context, events, ordinaryEvents = []) {
  const selected = scheduledIrrigationEventRows(context);
  const scheduled = (event) => selected.includes(Number(event.irrigation_index));
  const phaseEvents = context.phase.irrigation_events ?? [];
  const nextScheduled = events.filter(scheduled);
  const nextOrdinary = events.filter((event) => !scheduled(event));
  return {
    phase: _.isEqual(nextScheduled, phaseEvents.filter(scheduled)) ? undefined : [
      ...phaseEvents.filter((event) => !scheduled(event)), ...nextScheduled
    ],
    ordinary: _.isEqual(nextOrdinary, ordinaryEvents.filter((event) => !scheduled(event)))
      ? undefined : [...ordinaryEvents.filter(scheduled), ...nextOrdinary]
  };
}

export function availableScheduleFillingTanks(parent) {
  return (parent.settings.nutrition?.tanks ?? []).filter((tank) =>
    tank.setup_type === 1 && parent.settings.irrigation_tank_info?.some((info) =>
      info.tank_id === tank.tank_id && info.owner_zone === Number(parent.zone_id))
  );
}

export function scheduleFillingTanks(parent) {
  return availableScheduleFillingTanks(parent).map(makeScheduleTankFilling);
}
